// The Floor screen's server state (spec/13 §3, F-STN-14 … F-STN-18), as TanStack Query hooks over the
// generated client — `packages/contracts/src/routes/floor.ts`:
//
//   GET  /v1/organizations/{o}/venues/{v}/floor                        the live floor snapshot
//   POST …/stations/{stationId}/power                                  restart / … / wake
//   POST …/stations/{stationId}/lock                                   lock / unlock
//   POST …/stations/{stationId}/maintenance                            maintenance toggle
//
// **Liveness.** The floor is "kept current over the realtime channel" (spec/13 §3). The snapshot-then-
// stream sequencer (`@clavian/api-client`'s `createSnapshotStream`, spec/09 §4) is now driven by the
// console's own WebSocket client (`../realtime`, `floor/realtime.ts`): while the socket is live it
// applies `floor.station.updated` deltas into this cache and the interval below is switched off. When
// the socket is not live — reduced motion, or disconnected — the interval is the fallback (spec/09 §8),
// so the picture stays current either way. The snapshot returns the channel `version` the push needs.

import type { ApiClient, ClearStationAlertsRequest, OfflineCodeRequest, PutFloorPlan, StationLockRequest, StationMaintenanceRequest, StationMessageRequest, StationPowerRequest } from '@clavian/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useApi } from '../api/client';
import { useRealtimeLive } from '../realtime';

/** How often the snapshot is refetched while the Floor is open and the live push is not carrying it. */
const FLOOR_REFRESH_MS = 10_000;

export const floorKeys = {
  snapshot: (organizationId: string, venueId: string) => ['floor', organizationId, venueId] as const,
  installed: (organizationId: string, venueId: string, stationId: string) => ['floor', organizationId, venueId, 'installed', stationId] as const,
  wake: (organizationId: string, venueId: string, stationId: string) => ['floor', organizationId, venueId, 'wake', stationId] as const,
  calls: (organizationId: string, venueId: string) => ['calls', organizationId, venueId] as const,
  // Under the snapshot's key, so an alert pushed on `venue:{v}:floor` (outside the version sequence) refetches both.
  attention: (organizationId: string, venueId: string) => ['floor', organizationId, venueId, 'attention'] as const,
  health: (organizationId: string, venueId: string, stationId: string) => ['floor', organizationId, venueId, 'health', stationId] as const,
  // F-OPS-07: under the snapshot's key too, so `alerts.new` (N-23's floor alert) refetches the shift variances.
  shiftVariances: (organizationId: string, venueId: string) => ['floor', organizationId, venueId, 'shift-variances'] as const,
  telemetry: (organizationId: string, venueId: string, stationId: string) => ['floor', organizationId, venueId, 'telemetry', stationId] as const,
  // F-ANL-02: the floor's live figures — under the snapshot's key, so a floor alert's refetch reaches them too.
  figures: (organizationId: string, venueId: string) => ['floor', organizationId, venueId, 'figures'] as const,
  // F-STN-19: one floor's plan. `floor.plan.updated` on the floor channel invalidates it (`realtime.ts`).
  plans: (organizationId: string, venueId: string) => ['floor', organizationId, venueId, 'plan'] as const,
  plan: (organizationId: string, venueId: string, floor: number) => ['floor', organizationId, venueId, 'plan', floor] as const,
  // F-STN-21: the station's newest lockdown test.
  lockdownTest: (organizationId: string, venueId: string, stationId: string) => ['floor', organizationId, venueId, 'lockdown-test', stationId] as const,
};

/** How often the live figures are re-read with no session event to prompt it: a counter sale moves takings too. */
const FLOOR_LIVE_REFRESH_MS = 30_000;

/**
 * The floor's live figures (F-ANL-02, `GET …/venues/{v}/floor/live`, `shifts:day_close`): takings today by M-01 — the
 * figure the day's report shows — sessions today and now, occupancy now and open calls. Re-read on
 * `floor.session.started` / `ended` (`floor/realtime.ts`) and every 30 seconds for the sales that move takings.
 */
export function useFloorFigures(organizationId: string, venueId: string, enabled: boolean) {
  const api = useApi();
  return useQuery({
    queryKey: floorKeys.figures(organizationId, venueId),
    queryFn: () => api.getFloorLive({ path: { organizationId, venueId } }),
    enabled,
    refetchInterval: FLOOR_LIVE_REFRESH_MS,
  });
}

/** One floor's saved plan (F-STN-19, `GET …/floor-plan?floor=`): its grid, zones, fixtures and placed stations. */
export function useFloorPlan(organizationId: string, venueId: string, floor: number, enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: floorKeys.plan(organizationId, venueId, floor),
    queryFn: () => api.getFloorPlan({ path: { organizationId, venueId }, query: { floor } }),
    enabled,
  });
}

/**
 * Save one floor's plan (F-STN-19, `PUT …/floor-plan`), sending the version it was based on as `If-Match`. A `412`
 * surfaces as the error (someone saved first); on success the saved plan replaces the cached one.
 */
export function useSaveFloorPlan(organizationId: string, venueId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { readonly floor: number; readonly baseVersion: number; readonly body: PutFloorPlan }) =>
      api.putFloorPlan({
        path: { organizationId, venueId },
        query: { floor: input.floor },
        headerParams: { 'if-match': `"${input.baseVersion}"` },
        body: input.body,
      }),
    onSuccess: (plan) => {
      queryClient.setQueryData(floorKeys.plan(organizationId, venueId, plan.floor), plan);
    },
  });
}

/** The live floor snapshot: pushed while the socket is live, otherwise refreshed on an interval and on focus. */
export function useFloor(organizationId: string, venueId: string) {
  const api = useApi();
  const live = useRealtimeLive();
  return useQuery({
    queryKey: floorKeys.snapshot(organizationId, venueId),
    queryFn: () => api.getFloor({ path: { organizationId, venueId } }),
    refetchInterval: live ? false : FLOOR_REFRESH_MS,
    refetchOnWindowFocus: !live,
  });
}

/**
 * "Needs attention" (F-STN-15, spec/13 §3.2): the venue's open station alerts and the commands that failed or expired
 * (`GET …/venues/{v}/needs-attention`). Pushed alerts (`alerts.new` / `alerts.cleared` on the floor channel) refetch
 * it while the socket is live; otherwise it follows the floor's interval.
 */
export function useNeedsAttention(organizationId: string, venueId: string, enabled: boolean) {
  const api = useApi();
  const live = useRealtimeLive();
  return useQuery({
    queryKey: floorKeys.attention(organizationId, venueId),
    queryFn: () => api.getNeedsAttention({ path: { organizationId, venueId } }),
    enabled,
    refetchInterval: live ? false : FLOOR_REFRESH_MS,
    refetchOnWindowFocus: !live,
  });
}

/**
 * "Shift variance" items of "What needs you" (F-OPS-07): closed shifts whose variance alerted the owner and whose
 * business day is not closed yet (`GET …/shifts?varianceAlerted=true`, `shifts:read_all`). N-23's `alerts.new` on the
 * floor channel refetches it while the socket is live; otherwise it follows the floor's interval.
 */
export function useShiftVariances(organizationId: string, venueId: string, enabled: boolean) {
  const api = useApi();
  const live = useRealtimeLive();
  return useQuery({
    queryKey: floorKeys.shiftVariances(organizationId, venueId),
    queryFn: () => api.listShifts({ path: { organizationId, venueId }, query: { varianceAlerted: 'true' } }),
    enabled,
    refetchInterval: live ? false : FLOOR_REFRESH_MS,
    refetchOnWindowFocus: !live,
  });
}

/** A station's health (F-STN-11, spec/13 §3.3): open alerts, recent history and the latest reading. */
export function useStationHealth(organizationId: string, venueId: string, stationId: string, enabled: boolean) {
  const api = useApi();
  return useQuery({
    queryKey: floorKeys.health(organizationId, venueId, stationId),
    queryFn: () => api.getStationHealth({ path: { organizationId, venueId, stationId } }),
    enabled,
    staleTime: 15_000,
  });
}

/** A station's telemetry over the last day (F-STN-11), for the drawer's trend line. */
export function useStationTelemetry(organizationId: string, venueId: string, stationId: string, enabled: boolean) {
  const api = useApi();
  return useQuery({
    queryKey: floorKeys.telemetry(organizationId, venueId, stationId),
    queryFn: () => api.getStationTelemetry({ path: { organizationId, venueId, stationId }, query: { hours: 24 } }),
    enabled,
    staleTime: 60_000,
  });
}

/**
 * Which library titles a station has installed (F-CAT-03, spec/13 §3): `GET …/stations/{stationId}/installed`, the
 * station's last report against the titles its class is offered. The station reports only when its scan changes,
 * so a slow refresh is plenty. Only fetched when the viewer may read the library.
 */
export function useStationInstalled(organizationId: string, venueId: string, stationId: string, enabled: boolean) {
  const api = useApi();
  return useQuery({
    queryKey: floorKeys.installed(organizationId, venueId, stationId),
    queryFn: () => api.getStationInstalledTitles({ path: { organizationId, venueId, stationId } }),
    enabled,
    staleTime: 60_000,
  });
}

/**
 * Whether a station will wake from Wake-on-LAN (F-STN-17, spec/13 §3): `GET …/stations/{stationId}/wake-readiness`,
 * the station's last report and the addresses a wake uses. The station reports only on change, so a slow refresh
 * is plenty.
 */
export function useStationWakeStatus(organizationId: string, venueId: string, stationId: string, enabled: boolean) {
  const api = useApi();
  return useQuery({
    queryKey: floorKeys.wake(organizationId, venueId, stationId),
    queryFn: () => api.getStationWakeStatus({ path: { organizationId, venueId, stationId } }),
    enabled,
    staleTime: 60_000,
  });
}

interface CommandVars<TBody> {
  readonly stationId: string;
  readonly body: TBody;
}

function useStationCommand<TBody>(
  organizationId: string,
  venueId: string,
  call: (api: ApiClient, stationId: string, body: TBody) => Promise<unknown>,
) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ stationId, body }: CommandVars<TBody>) => call(api, stationId, body),
    // The command is queued and acked asynchronously; the interval refetch shows the station's new
    // mode once it reports it. Invalidate now so a returning focus does not wait for the interval.
    onSettled: () => queryClient.invalidateQueries({ queryKey: floorKeys.snapshot(organizationId, venueId) }),
  });
}

/** Power a station: restart / shutdown / sleep / wake (F-STN-17). */
export function useStationPower(organizationId: string, venueId: string) {
  return useStationCommand<StationPowerRequest>(organizationId, venueId, (api, stationId, body) =>
    api.stationPower({ path: { organizationId, venueId, stationId }, body }),
  );
}

/** Lock or unlock a station's screen (F-STN-16). */
export function useLockStation(organizationId: string, venueId: string) {
  return useStationCommand<StationLockRequest>(organizationId, venueId, (api, stationId, body) =>
    api.lockStation({ path: { organizationId, venueId, stationId }, body }),
  );
}

/** Put a station into or out of maintenance mode (F-STN-18). */
export function useSetStationMaintenance(organizationId: string, venueId: string) {
  return useStationCommand<StationMaintenanceRequest>(organizationId, venueId, (api, stationId, body) =>
    api.setStationMaintenance({ path: { organizationId, venueId, stationId }, body }),
  );
}

/** Skip a PC's start-up setup (F-STN-26, ADR-0055): it opens for play now. */
export function useSkipStationSetup(organizationId: string, venueId: string) {
  return useStationCommand<null>(organizationId, venueId, (api, stationId) => api.skipStationSetup({ path: { organizationId, venueId, stationId } }));
}

/** Message a player at a station (F-STN-20): a kiosk toast and in-game overlay, optionally requiring acknowledgement. */
export function useMessageStation(organizationId: string, venueId: string) {
  return useStationCommand<StationMessageRequest>(organizationId, venueId, (api, stationId, body) =>
    api.messageStation({ path: { organizationId, venueId, stationId }, body }),
  );
}

/**
 * Mint an offline code for a station (F-SES-24, spec/05 §8.4): the 8 digits staff read out while the
 * venue is offline. Nothing on the floor changes when one is minted, so nothing is invalidated.
 */
/** The station's newest lockdown test (F-STN-21); polled every 3 s while the PC has not answered. A 404 is "never tested". */
export function useStationLockdownTest(organizationId: string, venueId: string, stationId: string, enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: floorKeys.lockdownTest(organizationId, venueId, stationId),
    enabled,
    retry: false,
    queryFn: () => api.getStationLockdownTest({ path: { organizationId, venueId, stationId } }),
    refetchInterval: (query) => (query.state.data?.status === 'pending' ? 3_000 : false),
  });
}

/** Run the lockdown test on a station (F-STN-21, `station.lockdown.test`); the newest test is re-read. */
export function useRunLockdownTest(organizationId: string, venueId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (stationId: string) => api.runStationLockdownTest({ path: { organizationId, venueId, stationId } }),
    onSuccess: (test, stationId) => queryClient.setQueryData(floorKeys.lockdownTest(organizationId, venueId, stationId), test),
  });
}

/**
 * Clear or snooze a station's health alerts by hand (F-STN-11, 2026-10-02; `POST …/stations/{s}/alerts/clear`,
 * `stations:alerts_clear`): the ones named, or all of them, with an optional note. Re-reads everything under the floor's
 * key — the tile badge, "Needs attention" and the Health tab — at once; `alerts.cleared` on the floor channel does the
 * same for every other console.
 */
export function useClearStationAlerts(organizationId: string, venueId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ stationId, body }: CommandVars<ClearStationAlertsRequest>) => api.clearStationAlerts({ path: { organizationId, venueId, stationId }, body }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: floorKeys.snapshot(organizationId, venueId) }),
  });
}

/** Issue a one-time recovery code for `station-cli undo` on a station (F-LCK-06). Never cached. */
export function useIssueRecoveryCode(organizationId: string, venueId: string) {
  const api = useApi();
  return useMutation({
    mutationFn: (stationId: string) => api.issueStationRecoveryCode({ path: { organizationId, venueId, stationId } }),
  });
}

export function useGenerateOfflineCode(organizationId: string, venueId: string) {
  const api = useApi();
  return useMutation({
    mutationFn: ({ stationId, body }: CommandVars<OfflineCodeRequest>) =>
      api.generateOfflineCode({ path: { organizationId, venueId, stationId }, body }),
  });
}

// -----------------------------------------------------------------------------------------------
// Calls for help (F-SES-25, spec/13 §3.2). The venue's calls: kept live by `venue:{v}:calls` while the socket is
// live (`useCallsLive`, realtime.ts) — no polling then — and refreshed on the floor's interval when it is not (spec/09
// §8). Answering and resolving one re-reads the list.
// -----------------------------------------------------------------------------------------------

/** The venue's open and recently closed calls, newest first. */
export function useCalls(organizationId: string, venueId: string, enabled = true) {
  const api = useApi();
  const live = useRealtimeLive();
  return useQuery({
    queryKey: floorKeys.calls(organizationId, venueId),
    enabled,
    queryFn: () => api.listCalls({ path: { organizationId, venueId } }),
    refetchInterval: live ? false : FLOOR_REFRESH_MS,
    refetchOnWindowFocus: true,
  });
}

function useCallAction(organizationId: string, venueId: string, call: (api: ApiClient, callId: string) => Promise<unknown>) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ callId }: { readonly callId: string }) => call(api, callId),
    onSettled: () => queryClient.invalidateQueries({ queryKey: floorKeys.calls(organizationId, venueId) }),
  });
}

/** Answer a call — "On my way" (F-SES-25). */
export function useAcknowledgeCall(organizationId: string, venueId: string) {
  return useCallAction(organizationId, venueId, (api, callId) => api.acknowledgeCall({ path: { organizationId, venueId, callId } }));
}

/** Resolve a call — "Done" (F-SES-25). */
export function useResolveCall(organizationId: string, venueId: string) {
  return useCallAction(organizationId, venueId, (api, callId) => api.resolveCall({ path: { organizationId, venueId, callId } }));
}
